import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { CustomerCharacteristic } from '../../../customer/models/customer-characteristic.model';
import { CustomerCharacteristicType } from '../../../customer/models/enums/customer-characteristic-type.enum';

/**
 * This service provides front-end convenience methods for determining whether any redaction is needed.
 * Ideally, it should always be the back end which determines whether any redaction is needed, so the back end should be used when possible.
 * However, in the event that it is not feasible to do so, this service is meant as a fallback.
 * This service is intentionally written in a way that avoids making back-end calls, to the fullest extent possible.
 * If a back-end call ever is needed for any reason, then it's better to just use a back-end method to determine whether redaction is necessary.
 *
 * As of 07/11/2023, there are two types of redaction needed:
 *  1. Address Confidentiality Program (ACP) Customers need to have physical and domicile addresses redacted
 *  2. Restricted customers (see the "restrictedCharacteristicTypes" variable below) must have all personal identifiable information (PII) redacted.
 *
 * These requirements are subject to change in order to comply with changes in legislation, so make sure that the info above is current before use.
 **/
@Injectable()
export class RedactionService {
    private customerCharacteristicTypes = new CustomerCharacteristicType();

    // Contains all the different categories of redaction which might be needed
    private redactionTypes = {
        full: 1,
        residentialAddress: 2
    };

    // Restricted characteristic types indicate that a customer must have all personal identifiable information (PII) redacted
    private fullRedactionCharacteristicTypes = [
        this.customerCharacteristicTypes.OOPIAH.id,
        this.customerCharacteristicTypes.RestrictedRecord.id,
        this.customerCharacteristicTypes.OrgRestrictedRecord.id
    ];

    // Restricted physical address characteristic types indicate that a customer must have any physical and / or domicle addresses redacted
    private residentialAddressRedactionCharacteristicTypes = [
        this.customerCharacteristicTypes.AddressConfidentialityProgram.id
    ];

    // Associates each type of redaction with the set of all characteristics which indicate that such redaction is necessary
    private redactionTypeCharacteristicTypeMap: { [redactionType: number]: number[] };

    constructor() {
        this.redactionTypeCharacteristicTypeMap = this.buildRedactionTypeCharacteristicTypeMap();
    }

    /**
     * Checks to see whether the user has access to info that would normally be redacted.
     * @returns true if the user has access to info that would normally be redacted, false otherwise.
     * */
    public canViewRestrictedInfo(): boolean {
        const businessUnit = localStorage.getItem('mvd:bu');

        return !!businessUnit && businessUnit.indexOf('LECU') > 0;
    }

    /**
     * Checks whether personal identifiable information (PII) must be redacted
     * @param characteristics the array of all characteristics to check
     * @returns true if a restricted characteristic is found, false otherwise
     **/
    public isFullRedactionNecessary(characteristics: CustomerCharacteristic[]): boolean {
        return this.isRedactionNecessaryForRedactionType(characteristics, this.redactionTypes.full);
    }

    /**
     * Checks whether physical addresses or domicile addresses must be redacted
     * @param characteristics the array of all characteristics to check
     * @returns true if a restricted characteristic is found, false otherwise
     **/
    public isResidentialAddressRedactionNecessary(characteristics: CustomerCharacteristic[]): boolean {
        return this.isRedactionNecessaryForRedactionType(characteristics, this.redactionTypes.residentialAddress);
    }

    /**
     * Creates an association between each type of redaction and the set of all characteristics which indicate that such redaction is necessary
     * @returns the dictionary which maps redaction types to characteristic arrays
     **/
    private buildRedactionTypeCharacteristicTypeMap(): {[redactionType: number]: number[]} {
        const redactionCharacteristicMap = {};
        redactionCharacteristicMap[this.redactionTypes.full] = this.fullRedactionCharacteristicTypes;
        redactionCharacteristicMap[this.redactionTypes.residentialAddress] = this.residentialAddressRedactionCharacteristicTypes;

        return redactionCharacteristicMap;
    }

    /**
     * Checks whether a characteristic is effective (that is, it has already been applied to the user and has not yet expired)
     * @param characteristic the characteristic to be checked
     * @returns true if the characteristic is effective, false otherwise
     **/
    private isCharacteristicEffective(characteristic: CustomerCharacteristic): boolean {
        const now = moment();
        const isEffectiveDateValid = moment(characteristic.effectiveDate) <= now;
        const isEndDateValid = !characteristic.endDate || moment(characteristic.endDate) > now;

        return isEffectiveDateValid && isEndDateValid;
    }

    /**
     * Checks whether a certain category of redaction is applicable
     * @param characteristics the array of all characteristics to check
     * @returns true if a restricted characteristic is found, false otherwise
     **/
    private isRedactionNecessaryForRedactionType(characteristics: CustomerCharacteristic[], redactionType: number): boolean {
        if (!characteristics || this.canViewRestrictedInfo()) {
            return false;
        }

        const restrictedCharacteristicTypes = this.redactionTypeCharacteristicTypeMap[redactionType];
        for (const characteristic of characteristics) {
            const isCharacteristicEffective = this.isCharacteristicEffective(characteristic);
            const isRestricted = restrictedCharacteristicTypes.includes(characteristic.customerCharacteristicTypeId);

            if (isCharacteristicEffective && isRestricted) {
                return true;
            }
        }

        return false;
    }
}
